import { PATH_SEPARATOR } from '../query/match-query';
import type {
  AppSearchAction,
  AppSearchNavNode,
  AppSearchProvider,
} from '../types';

export const NAV_SEARCH_PROVIDER_ID = 'nav';

const isNavigateable = (node: AppSearchNavNode) => Boolean(node.component);

export const getNavSearchActions = (
  tree: AppSearchNavNode[] | null | undefined,
  {
    navigate,
    t,
    completeSearch,
  }: {
    navigate: (to: string) => void;
    t: (key: string) => string;
    completeSearch: (action: AppSearchAction) => void;
  },
): AppSearchAction[] => {
  const walk = (
    nodes: AppSearchNavNode[] = [],
    path: string[] = [],
    pathKeys: string[] = [],
  ): AppSearchAction[] => {
    const actions: AppSearchAction[] = [];

    for (const node of nodes) {
      const labelKey = node.label;
      const label = labelKey ? t(labelKey) : '';
      const children = node.children ?? [];

      if (!label) {
        if (children.length) {
          actions.push(...walk(children, path, pathKeys));
        }
        continue;
      }

      const navigateable = isNavigateable(node);
      const nextPath = [...path, label];
      const nextPathKeys = labelKey ? [...pathKeys, labelKey] : pathKeys;

      if (!navigateable && children.length && path.length > 0) {
        actions.push(
          ...walk(children, path, pathKeys).map((child) => ({
            ...child,
            description: nextPath.join(PATH_SEPARATOR),
            keywords: [...(child.keywords ?? []), label],
          })),
        );
        continue;
      }

      const nested = children.length
        ? walk(children, nextPath, nextPathKeys)
        : [];
      const action: AppSearchAction = {
        id: `nav:${node.to ?? nextPath.join('/')}`,
        label,
        labelKey,
        description: path.join(PATH_SEPARATOR) || undefined,
        path: nextPath,
        pathKeys: nextPathKeys,
        completion: nextPath.join(PATH_SEPARATOR),
        enterable: nested.length > 0,
        to: navigateable ? node.to : undefined,
        href: navigateable ? node.to : undefined,
        keywords: [label, node.label, node.to, ...nextPath].filter(
          (value): value is string => Boolean(value),
        ),
        icon: node.icon,
        children: nested,
        onSelect: () => {
          if (navigateable && node.to) {
            navigate(node.to);
            return;
          }

          completeSearch(action);
        },
      };

      actions.push(action);
    }

    return actions;
  };

  return walk(tree ?? []);
};

export const navSearchProvider: AppSearchProvider = {
  id: NAV_SEARCH_PROVIDER_ID,
  order: 0,
  getActions: ({ navigation, navigate, t, completeSearch }) =>
    getNavSearchActions(navigation, { navigate, t, completeSearch }),
};